import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { waLink } from "@/lib/contactLinks";
import Brand from "@/components/site/Brand";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (item) => {
    setOpen(false);
    if (item.type === "route") {
      navigate(item.to);
      return;
    }
    if (location.pathname !== "/") navigate(`/#${item.href}`);
    else scrollToId(item.href);
  };

  const goInquiry = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/#inquiry");
    else scrollToId("inquiry");
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass border-b border-white/10 py-2" : "bg-transparent py-3"}`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <Brand onClick={() => navigate("/")} testId="brand-logo" />

        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV_ITEMS.map((item) =>
            item.type === "route" ? (
              <Link key={item.label} to={item.to} data-testid={item.testId} onClick={() => setOpen(false)}
                className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors">
                {item.label}
              </Link>
            ) : (
              <button key={item.label} onClick={() => go(item)} data-testid={item.testId}
                className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors">
                {item.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <a href={waLink()} target="_blank" rel="noreferrer" data-testid="nav-whatsapp-button"
            className="hidden sm:grid place-items-center h-10 w-10 rounded-full bg-[#25D366] text-white hover:scale-105 transition-transform" aria-label="WhatsApp">
            <MessageCircle className="h-5 w-5" />
          </a>
          <button onClick={goInquiry} data-testid="sticky-enquire-now-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-2.5 text-sm font-semibold text-[#1a0f00] shadow-[0_8px_24px_rgba(245,158,11,0.35)] hover:-translate-y-0.5 transition-all duration-300">
            Enquire Now
          </button>
          <button className="lg:hidden grid place-items-center h-10 w-10 rounded-lg glass text-white" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden glass border-t border-white/10 mt-2" data-testid="mobile-menu">
            <div className="px-4 py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) =>
                item.type === "route" ? (
                  <Link key={item.label} to={item.to} onClick={() => setOpen(false)} className="text-left px-3 py-3 rounded-lg text-slate-200 hover:bg-white/5 hover:text-white transition-colors">{item.label}</Link>
                ) : (
                  <button key={item.label} onClick={() => go(item)} className="text-left px-3 py-3 rounded-lg text-slate-200 hover:bg-white/5 hover:text-white transition-colors">{item.label}</button>
                )
              )}
              <div className="mt-2 flex gap-2">
                <a href={waLink()} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-[#052e16]"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
                <button onClick={goInquiry} className="flex-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-3 text-sm font-semibold text-[#1a0f00]">Enquire Now</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
